import interpolateComponents from '@automattic/interpolate-components';
import { useEffect, useRef, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { BodyMicro } from 'components/Text/BodyMicro';
import { H4 } from 'components/Text/H4';
import { Axis } from 'modules/salaries/components/Axis/Axis';
import { Bar } from 'modules/salaries/components/Bar/Bar';
import { Line } from 'modules/salaries/components/Line/Line';
import { MainChartLegend } from 'modules/salaries/components/MainChartLegend/MainChartLegend';
import { normalizeDataToPixel } from 'modules/salaries/helpers/normalizeDataToPixel';
import { MainChartData } from 'modules/salaries/types/MainChartData';
import { colors } from 'theme/theme';
import { objectEmpty } from 'utils/functional/object';
import { isDefined } from 'utils/functional/type';
import { getRgbaString } from 'utils/style';

import {
  BarContainer,
  ChartDataContainer,
  ChartDataWrapper,
  HorizontalAxisWrapper,
  MainChartContainer,
  MainChartWrapper,
  TooltipContainer,
} from './MainChart.styled';

const BAR_PADDING = '25%';
const CHART_HEIGHT = 240;

function formatSalary(salary: number): string {
  return salary.toLocaleString(CURRENT_LOCALE(), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

type ChartProps = {
  data: MainChartData;
  setData: (arg0: MainChartData) => void;
  loading: boolean;
  initialized: boolean;
};

export function MainChart({ data, setData, initialized, loading }: ChartProps) {
  const [chartWidth, setChartWidth] = useState<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () =>
      setChartWidth(
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        chartRef && chartRef.current
          ? chartRef.current.getBoundingClientRect().width
          : 0,
      );

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [setChartWidth]);

  const { barChart, lineChart, xTicks } = data || {};
  // @ts-expect-error TS(2345): Argument of type 'Record<string, never> | LineChar... Remove this comment to see the full error message
  const hasLineChart = isDefined(lineChart) && !objectEmpty(lineChart);
  // @ts-expect-error TS(2345): Argument of type 'BarChart | Record<string, never>... Remove this comment to see the full error message
  const hasBarChart = isDefined(barChart) && !objectEmpty(barChart);

  // @ts-expect-error TS(2345): Argument of type 'BarChart | Record<string, never>... Remove this comment to see the full error message
  let xRangeMin = !objectEmpty(barChart)
    ? barChart.xRangeMin
    : lineChart.xRangeMin;
  // @ts-expect-error TS(2345): Argument of type 'BarChart | Record<string, never>... Remove this comment to see the full error message
  let xRangeMax = !objectEmpty(barChart)
    ? barChart.xRangeMax
    : lineChart.xRangeMax;
  const yRangeMax = hasBarChart ? barChart.yRangeMax : lineChart.yRangeMax;
  // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
  const xGridLines: number = hasBarChart
    ? barChart.xGridLines
    : lineChart.xGridLines;
  // @ts-expect-error TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
  const yGridLines: number = hasBarChart
    ? barChart.yGridLines
    : lineChart.yGridLines;

  if (hasBarChart && hasLineChart && xRangeMax < lineChart.xRangeMax) {
    xRangeMax = lineChart.xRangeMax;
  }

  if (hasBarChart && hasLineChart && xRangeMin > lineChart.xRangeMin) {
    xRangeMin = lineChart.xRangeMin;
  }

  let hoverSections = {};
  if (hasLineChart) {
    const xMinCoord = normalizeDataToPixel(
      lineChart.xRangeMin,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );

    const { sectionLimits } = lineChart;
    const {
      // @ts-expect-error TS(2339): Property 'middle68LowerBound' does not exist on ty... Remove this comment to see the full error message
      middle68LowerBound,
      // @ts-expect-error TS(2339): Property 'middle68UpperBound' does not exist on ty... Remove this comment to see the full error message
      middle68UpperBound,
      // @ts-expect-error TS(2339): Property 'middle95LowerBound' does not exist on ty... Remove this comment to see the full error message
      middle95LowerBound,
      // @ts-expect-error TS(2339): Property 'middle95UpperBound' does not exist on ty... Remove this comment to see the full error message
      middle95UpperBound,
    } = sectionLimits;

    const lowerBound95Coord = normalizeDataToPixel(
      middle95LowerBound,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );

    const upperBound95Coord = normalizeDataToPixel(
      middle95UpperBound,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );

    const lowerBound68Coord = normalizeDataToPixel(
      middle68LowerBound,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );

    const upperBound68Coord = normalizeDataToPixel(
      middle68UpperBound,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );
    const xMaxCoord = normalizeDataToPixel(
      lineChart.xRangeMax,
      xRangeMin,
      xRangeMax,
      chartWidth - 80,
    );

    hoverSections = {
      lowerBound: {
        coords: `${xMinCoord} 0, ${xMinCoord} ${
          CHART_HEIGHT - 4
        }, ${lowerBound95Coord} ${CHART_HEIGHT - 4}, ${lowerBound95Coord} 0`,
        tooltip: (
          <TooltipContainer>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  '{{strong}}2.5%%{{/strong}} of people make less than {{strong}}%(middle95LowerBound)s{{/strong}}',
                ),
                {
                  middle95LowerBound: formatSalary(middle95LowerBound),
                },
              ),
              components: {
                strong: <strong />,
              },
            })}
          </TooltipContainer>
        ),
      },
      interval95: {
        coords: `${lowerBound95Coord} 0, ${lowerBound95Coord} ${
          CHART_HEIGHT - 4
        }, ${upperBound95Coord} ${CHART_HEIGHT - 4}, ${upperBound95Coord} 0`,
        tooltip: (
          <TooltipContainer>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  '{{strong}}95%%{{/strong}} of people make between {{strong}}%(middle95LowerBound)s{{/strong}} and {{strong}}%(middle95UpperBound)s{{/strong}}',
                ),
                {
                  middle95LowerBound: formatSalary(middle95LowerBound),
                  middle95UpperBound: formatSalary(middle95UpperBound),
                },
              ),
              components: {
                strong: <strong />,
              },
            })}
          </TooltipContainer>
        ),
      },
      interval68: {
        coords: `${lowerBound68Coord} 0, ${lowerBound68Coord} ${
          CHART_HEIGHT - 4
        }, ${upperBound68Coord} ${CHART_HEIGHT - 4}, ${upperBound68Coord} 0`,
        tooltip: (
          <TooltipContainer>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  '{{strong}}68%%{{/strong}} of people make between {{strong}}%(middle68LowerBound)s{{/strong}} and {{strong}}%(middle68UpperBound)s{{/strong}}',
                ),
                {
                  middle68LowerBound: formatSalary(middle68LowerBound),
                  middle68UpperBound: formatSalary(middle68UpperBound),
                },
              ),
              components: {
                strong: <strong />,
              },
            })}
          </TooltipContainer>
        ),
      },
      upperBound: {
        coords: `${upperBound95Coord} 0, ${upperBound95Coord} ${
          CHART_HEIGHT - 4
        }, ${xMaxCoord} ${CHART_HEIGHT - 4}, ${xMaxCoord} 0`,
        tooltip: (
          <TooltipContainer>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  '{{strong}}2.5%%{{/strong}} of people make more than {{strong}}%(middle95UpperBound)s{{/strong}}',
                ),
                {
                  middle95UpperBound: formatSalary(middle95UpperBound),
                },
              ),
              components: {
                strong: <strong />,
              },
            })}
          </TooltipContainer>
        ),
      },
    };
  }

  return (
    <MainChartContainer
      data-qa-id={loading || !initialized ? 'preview-main-chart' : 'main-chart'}
      ref={chartRef}
    >
      <MainChartWrapper>
        <ChartDataContainer $initialized={initialized} $loading={loading}>
          <ChartDataWrapper
            chartWidth={chartWidth - 80}
            $xGridInterval={(chartWidth - 80) / (xGridLines - 1) - 0.2}
            $yGridInterval={240 / yGridLines}
          >
            {hasLineChart && chartWidth ? (
              <Line
                fillColor={`rgba(${getRgbaString(colors.brandBlue, 0.5)})`}
                qaId={
                  lineChart.active
                    ? 'estimated-range-layer-active'
                    : 'estimated-range-layer'
                }
                chartHeight={CHART_HEIGHT - 4}
                chartWidth={chartWidth - 80}
                data={lineChart.points}
                minX={xRangeMin}
                maxX={xRangeMax}
                maxY={yRangeMax}
                active={lineChart.active}
                initialized={initialized}
                hoverSections={hoverSections}
                hoverSectionColor={colors.brandBlue}
                loading={loading}
              />
            ) : null}
            {hasBarChart && chartWidth
              ? barChart.data.map((salary, index) =>
                  salary.count > 0 ? (
                    <BarContainer
                      data-qa-id={
                        barChart.active
                          ? 'submissions-layer-active'
                          : 'submissions-layer'
                      }
                      // eslint-disable-next-line react/no-array-index-key
                      key={index * barChart.data.length}
                      $bucketCount={1 / barChart.data.length}
                    >
                      <Bar
                        $animationDelay={300}
                        $loading={loading}
                        $active={barChart.active}
                        initialized={initialized}
                        // @ts-expect-error TS(2769): No overload matches this call.
                        index={salary.animationIndex}
                        offset={0}
                        bucketCount={barChart.data.length}
                        height={`${Math.round(
                          (salary.count / barChart.yRangeMax) * 100,
                        )}%`}
                        tooltip={
                          <TooltipContainer>
                            <H4 mb={0}>{salary.salaryRange}</H4>
                            <BodyMicro mt="-6px" opacity={0.8}>
                              {salary.numberOfSubmissions}
                            </BodyMicro>
                          </TooltipContainer>
                        }
                        showTooltip={barChart.active}
                        width={`calc(100% - ${BAR_PADDING})`}
                      />
                    </BarContainer>
                  ) : (
                    <BarContainer
                      // eslint-disable-next-line react/no-array-index-key
                      key={index * barChart.data.length}
                      $bucketCount={1 / barChart.data.length}
                    />
                  ),
                )
              : null}
          </ChartDataWrapper>
        </ChartDataContainer>
        {chartWidth ? (
          <HorizontalAxisWrapper>
            <Axis
              alignToGrid
              direction="x"
              gridLines={xGridLines - 1}
              chartWidth={chartWidth}
              ticks={xTicks.map((t) => ({ name: t, component: t }))}
              type="range"
            />
          </HorizontalAxisWrapper>
        ) : null}
      </MainChartWrapper>
      {initialized && !loading && data !== null ? (
        <MainChartLegend data={data} setData={setData} />
      ) : null}
    </MainChartContainer>
  );
}
